import { getAccessToken } from "../../shared/utils/secureTokenStorage";

const API_BASE = process.env.REACT_APP_API_BASE || "";

/**
 * Chat Usage Service
 * Handles credit balance, usage tracking, and reporting APIs
 */

// Credit costs by mode
export const CREDITS_PER_CHAT = 25; // Default for Case and AMA modes
export const CREDITS_PER_MARKETING_CHAT = 100; // Marketing Assistant

// Credit costs lookup by mode
export const CREDITS_BY_MODE = {
  case: 25,
  ama: 25,
  marketing: 100,
  help: 0, // Free
};

// Credit warning thresholds
export const CREDIT_THRESHOLDS = {
  LOW: 100,      // Show warning
  CRITICAL: 25,  // Show urgent warning
};

/**
 * Get user's credit balance
 * @returns {Promise<{credits_balance: number, credits_per_chat: number, estimated_chats_remaining: number}>}
 */
export const fetchCreditBalance = async () => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/user/chat-usage/credits`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch credit balance");
  }

  return response.json();
};

/**
 * Get usage summary for a date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<UsageSummary>}
 */
export const fetchUsageSummary = async (startDate, endDate) => {
  const token = getAccessToken();
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  const response = await fetch(
    `${API_BASE}/api/user/chat-usage/summary?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch usage summary");
  }

  return response.json();
};

/**
 * Get detailed usage history
 * @param {Object} options
 * @param {number} options.limit - Number of records to return
 * @param {string} options.mode - Filter by mode (marketing, case, ama)
 * @returns {Promise<UsageHistory>}
 */
export const fetchUsageHistory = async ({ limit = 50, mode } = {}) => {
  const token = getAccessToken();
  const params = new URLSearchParams();
  params.append("limit", limit.toString());
  if (mode) params.append("mode", mode);

  const response = await fetch(
    `${API_BASE}/api/user/chat-usage/history?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch usage history");
  }

  return response.json();
};

/**
 * Admin: Get aggregate usage summary
 * @param {string} startDate
 * @param {string} endDate
 * @returns {Promise<AdminUsageSummary>}
 */
export const fetchAdminUsageSummary = async (startDate, endDate) => {
  const token = getAccessToken();
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  const response = await fetch(
    `${API_BASE}/api/admin/chat-usage/summary?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch admin usage summary");
  }

  return response.json();
};

/**
 * Admin: Get daily usage breakdown
 * @param {string} startDate
 * @param {string} endDate
 * @returns {Promise<DailyUsage[]>}
 */
export const fetchAdminDailyUsage = async (startDate, endDate) => {
  const token = getAccessToken();
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  const response = await fetch(
    `${API_BASE}/api/admin/chat-usage/daily?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch daily usage");
  }

  return response.json();
};

/**
 * Admin: Export usage data
 * @param {string} format - 'csv' or 'json'
 * @param {string} startDate
 * @param {string} endDate
 * @returns {Promise<Blob>}
 */
export const exportUsageData = async (format = "csv", startDate, endDate) => {
  const token = getAccessToken();
  const params = new URLSearchParams();
  params.append("format", format);
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  const response = await fetch(
    `${API_BASE}/api/admin/chat-usage/export?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to export usage data");
  }

  return response.blob();
};

/**
 * Get credit status color based on remaining credits
 * @param {number} credits
 * @returns {'green' | 'yellow' | 'red'}
 */
export const getCreditStatusColor = (credits) => {
  if (credits <= CREDIT_THRESHOLDS.CRITICAL) return "red";
  if (credits <= CREDIT_THRESHOLDS.LOW) return "yellow";
  return "green";
};

/**
 * Format credits for display
 * @param {number} credits
 * @returns {string}
 */
export const formatCredits = (credits) => {
  if (credits >= 1000) {
    return `${(credits / 1000).toFixed(1)}k`;
  }
  return credits.toLocaleString();
};

/**
 * Calculate estimated chats remaining
 * @param {number} credits
 * @param {string} mode - Optional mode ('case', 'ama', 'marketing')
 * @returns {number}
 */
export const getEstimatedChatsRemaining = (credits, mode = 'case') => {
  const costPerChat = CREDITS_BY_MODE[mode] || CREDITS_PER_CHAT;
  if (costPerChat === 0) return Infinity;
  return Math.floor(credits / costPerChat);
};

/**
 * Parse insufficient credits error from API response
 * @param {Object} errorData
 * @returns {{required: number, available: number, message: string} | null}
 */
export const parseInsufficientCreditsError = (errorData) => {
  if (errorData?.error === "insufficient_credits") {
    return {
      required: errorData.credits_required || CREDITS_PER_CHAT,
      available: errorData.credits_available || 0,
      message: errorData.message || "Insufficient credits",
    };
  }
  return null;
};

/**
 * Fetch AI Reports analytics
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<AIReportsAnalytics>}
 */
export const fetchAIReportsAnalytics = async (startDate, endDate) => {
  const token = getAccessToken();
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  const response = await fetch(
    `${API_BASE}/api/user/chat-usage/ai-reports?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch AI reports analytics");
  }

  return response.json();
};

// Mode colors for charts
export const MODE_COLORS = {
  marketing: "#8B5CF6", // Purple
  case: "#3B82F6",      // Blue
  ama: "#10B981",       // Green
  case_assistant: "#3B82F6", // Blue (alias)
};

// Mode labels for display
export const MODE_LABELS = {
  marketing: "Marketing Assistant",
  case: "Case Assistant",
  case_assistant: "Case Assistant",
  ama: "AMA",
};

// AI Report type colors
export const REPORT_TYPE_COLORS = {
  basic: "#3B82F6",     // Blue
  advanced: "#8B5CF6",  // Purple
};

// AI Report status colors
export const REPORT_STATUS_COLORS = {
  PENDING: "#6B7280",   // Gray
  RUNNING: "#F59E0B",   // Amber
  SUCCEEDED: "#10B981", // Green
  FAILED: "#EF4444",    // Red
};

// AI Report delivery labels
export const DELIVERY_LABELS = {
  standard: "Standard",
  expedited: "Expedited",
  ultra: "Ultra Fast",
};

/**
 * Get combined usage dashboard data (chat + AI reports)
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {number} limit - Number of recent activity items
 * @returns {Promise<CombinedUsageData>}
 */
export const fetchCombinedUsage = async (startDate, endDate, limit = 50) => {
  const token = getAccessToken();
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  params.append("limit", limit.toString());

  const response = await fetch(
    `${API_BASE}/api/user/chat-usage/combined?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch combined usage data");
  }

  return response.json();
};
